import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

// Utilidades
const parseDate = (dateStr: string | null): Date | null => {
  if (!dateStr) return null;
  // Espera formato YYYY-MM-DD
  const [y, m, d] = dateStr.split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const parseTime = (
  timeStr: string | Date | null
): { h: number; mi: number; s: number } | null => {
  if (!timeStr) return null;
  
  // Si es un objeto Date, extraer horas, minutos y segundos
  if (timeStr instanceof Date) {
    return {
      h: timeStr.getHours(),
      mi: timeStr.getMinutes(),
      s: timeStr.getSeconds()
    };
  }
  
  // Si es string, usar el método original
  const [h, mi, s] = timeStr.split(':').map((n) => parseInt(n, 10));
  if (Number.isNaN(h) || Number.isNaN(mi) || Number.isNaN(s)) return null;
  return { h, mi, s };
};

const toDateTime = (
  dateStr: string | null,
  timeStr: string | Date | null
): Date | null => {
  const d = parseDate(dateStr);
  const t = parseTime(timeStr);
  if (!d || !t) return null;
  const dt = new Date(d);
  dt.setHours(t.h, t.mi, t.s, 0);
  return dt;
};

// 1) start <= end
export const startBeforeOrEqualEndValidator: ValidatorFn = (
  group: AbstractControl
): ValidationErrors | null => {
  const startDateStr = group.get('start_date')?.value ?? null;
  const endDateStr = group.get('end_date')?.value ?? null;
  const startTimeValue = group.get('time_start')?.value ?? null;
  const endTimeValue = group.get('time_end')?.value ?? null;

  const startDate = parseDate(startDateStr);
  const endDate = parseDate(endDateStr);
  if (!startDate || !endDate) return null; // otros validadores se encargarán de required/formato

  // Primero comparamos fechas
  const startDateMs = startDate.setHours(0, 0, 0, 0);
  const endDateMs = endDate.setHours(0, 0, 0, 0);
  if (startDateMs < endDateMs) return null;
  if (startDateMs > endDateMs)
    return { startAfterEnd: { reason: 'date' as const } };

  // Si las fechas son iguales, comparamos horas
  const startTime = parseTime(startTimeValue);
  const endTime = parseTime(endTimeValue);
  if (!startTime || !endTime) return null;
  const toSeconds = (t: { h: number; mi: number; s: number }) =>
    t.h * 3600 + t.mi * 60 + t.s;
  const startSeconds = toSeconds(startTime);
  const endSeconds = toSeconds(endTime);
  return startSeconds <= endSeconds
    ? null
    : { startAfterEnd: { reason: 'time' as const } };
};

// 2) max 90 días hacia atrás respecto a ahora para start y end
export const notOlderThanNDaysValidator = (maxDays: number): ValidatorFn => {
  return (group: AbstractControl): ValidationErrors | null => {
    const startDate = group.get('start_date')?.value ?? null;
    const endDate = group.get('end_date')?.value ?? null;
    const startTimeValue = group.get('time_start')?.value;
    const endTimeValue = group.get('time_end')?.value;

    // Usar valores por defecto apropiados si no hay valores
    const startTime = startTimeValue ?? '00:00:00';
    const endTime = endTimeValue ?? '23:59:59';

    const start = toDateTime(startDate, startTime);
    const end = toDateTime(endDate, endTime);
    if (!start || !end) return null;

    const now = new Date();
    const maxPast = new Date(now);
    maxPast.setDate(now.getDate() - maxDays);

    const valid = start >= maxPast && end >= maxPast;
    return valid ? null : { olderThanAllowed: { maxDays } };
  };
};

// 3) rango máximo 15 días entre start y end (fechas)
export const maxRangeDaysValidator = (maxRangeDays: number): ValidatorFn => {
  return (group: AbstractControl): ValidationErrors | null => {
    const startDate = parseDate(group.get('start_date')?.value ?? null);
    const endDate = parseDate(group.get('end_date')?.value ?? null);
    if (!startDate || !endDate) return null;

    const msPerDay = 24 * 60 * 60 * 1000;
    const diffDays = Math.floor(
      (endDate.getTime() - startDate.getTime()) / msPerDay
    );
    return diffDays <= maxRangeDays
      ? null
      : { rangeTooLarge: { maxRangeDays } };
  };
};

// 4) Validación de formato básico HH:MM:SS
export const timeFormatValidator = (
  control: AbstractControl
): ValidationErrors | null => {
  const v = control.value;
  if (!v) return null;
  
  // Si es un objeto Date, es válido
  if (v instanceof Date) {
    return null;
  }
  
  // Si es string, validar formato HH:MM:SS
  if (typeof v === 'string') {
    const regex = /^([01]?\d|2[0-3]):([0-5]?\d):([0-5]?\d)$/;
    return regex.test(v) ? null : { invalidTimeFormat: true };
  }
  
  // Si no es Date ni string, es inválido
  return { invalidTimeFormat: true };
};
